import { Badge, Button, Drawer, Space, Table, Tag, message } from 'antd';
import { useEffect, useState } from 'react';
import { listShipmentBatches, getShipmentBatch, retryShipmentBatch } from '../api/orders.api';

/**
 * Toolbar chip that shows how many shipment batches are currently running
 * and opens a drawer with the full list + detail viewer + retry. Meant to
 * live in the Shipments page toolbar. Polls the backend so any admin sees
 * activity started by other operators.
 */
export default function BatchStatusButton() {
  const [list, setList] = useState<any[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [detail, setDetail] = useState<any>(null);

  // Poll cadence is driven only by whether any batch is actually running —
  // opening the drawer doesn't accelerate it. When a batch is active the
  // per-batch detail poll (below) already refreshes the selected job every
  // 2s; the list itself just needs to keep the badge count fresh.
  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const { jobs } = await listShipmentBatches();
        if (cancelled) return;
        setList(jobs);
        const anyRunning = jobs.some((j) => j.status === 'pending' || j.status === 'processing');
        setTimeout(() => { if (!cancelled) void load(); }, anyRunning ? 5000 : 30000);
      } catch {
        setTimeout(() => { if (!cancelled) void load(); }, 30000);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!selectedJobId) return;
    let cancelled = false;
    const poll = async (): Promise<void> => {
      try {
        const job = await getShipmentBatch(selectedJobId);
        if (cancelled) return;
        setDetail(job);
        if (job.status === 'pending' || job.status === 'processing') {
          setTimeout(() => { if (!cancelled) void poll(); }, 2000);
        }
      } catch {
        setTimeout(() => { if (!cancelled) void poll(); }, 5000);
      }
    };
    void poll();
    return () => { cancelled = true; };
  }, [selectedJobId]);

  const active = list.filter((b) => b.status === 'pending' || b.status === 'processing');

  const onRetry = async (): Promise<void> => {
    if (!selectedJobId) return;
    try {
      const { retried } = await retryShipmentBatch(selectedJobId);
      if (retried === 0) { message.info('No failed orders to retry.'); return; }
      message.success(`Retrying ${retried} order(s)...`);
    } catch (err) {
      message.error(`Retry failed: ${(err as Error).message ?? 'unknown'}`);
    }
  };

  return (
    <>
      <Badge count={active.length} size="small" offset={[-4, 4]}>
        <Button
          type="dashed"
          onClick={() => setDrawerOpen(true)}
          style={{
            color: active.length > 0 ? '#1890ff' : undefined,
            borderColor: active.length > 0 ? '#1890ff' : undefined,
          }}
        >
          {active.length > 0 ? `Batches (${active.length} running)` : 'Batches'}
        </Button>
      </Badge>

      <Drawer
        title="Shipment Batches"
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setSelectedJobId(null); setDetail(null); }}
        width={selectedJobId ? 900 : 720}
      >
        {!selectedJobId ? (
          <Table
            size="small"
            pagination={false}
            rowKey="id"
            dataSource={list}
            locale={{ emptyText: 'No batches in the last 24 hours. Start one from Orders → Ship Selected.' }}
            columns={[
              { title: 'Started', dataIndex: 'created_at', width: 160, render: (v: string) => new Date(v).toLocaleString() },
              {
                title: 'Status',
                dataIndex: 'status',
                width: 110,
                render: (s: string) =>
                  s === 'processing' || s === 'pending' ? <Tag color="blue">{s}</Tag>
                  : s === 'complete' ? <Tag color="green">complete</Tag>
                  : <Tag color="red">failed</Tag>,
              },
              {
                title: 'Progress',
                width: 200,
                render: (_: any, r: any) => (
                  <span>
                    {r.succeeded + r.failed + r.skipped}/{r.total}
                    {r.failed > 0 && <span style={{ color: '#cf1322', marginLeft: 6 }}>{r.failed} failed</span>}
                    {r.skipped > 0 && <span style={{ color: '#d46b08', marginLeft: 6 }}>{r.skipped} skipped</span>}
                  </span>
                ),
              },
              { title: 'By User', dataIndex: 'user_id', width: 80, render: (v: number | null) => v ?? '-' },
              { title: '', width: 80, render: (_: any, r: any) => <Button size="small" onClick={() => setSelectedJobId(r.id)}>Open</Button> },
            ]}
          />
        ) : (
          <>
            <Space style={{ marginBottom: 12 }}>
              <Button onClick={() => { setSelectedJobId(null); setDetail(null); }}>← Back</Button>
              {detail?.failed > 0 && <Button onClick={onRetry}>Retry {detail.failed} Failed</Button>}
            </Space>
            {detail && (
              <>
                <div style={{ marginBottom: 12, padding: 10, background: '#fafafa', border: '1px solid #eee', borderRadius: 4 }}>
                  <div><strong>Status:</strong> {detail.status}</div>
                  <div style={{ marginTop: 4, fontSize: 13 }}>
                    {detail.succeeded + detail.failed + detail.skipped} of {detail.total} processed
                    — {detail.succeeded} ok, {detail.failed} failed, {detail.skipped} skipped
                  </div>
                  {detail.error_message && <div style={{ marginTop: 6, color: '#cf1322', fontSize: 12 }}>{detail.error_message}</div>}
                </div>
                <Table
                  size="small"
                  pagination={false}
                  rowKey={(r) => (r as any).orderId}
                  dataSource={Object.entries(detail.per_order_state ?? {}).map(([orderId, s]: [string, any]) => ({ orderId, ...s }))}
                  columns={[
                    { title: 'Order ID', dataIndex: 'orderId', width: 120 },
                    {
                      title: 'Status', dataIndex: 'status', width: 100,
                      render: (s: string) =>
                        s === 'success' ? <Tag color="green">Success</Tag>
                        : s === 'error' ? <Tag color="red">Failed</Tag>
                        : s === 'skipped' ? <Tag color="orange">Skipped</Tag>
                        : <Tag>Pending</Tag>,
                    },
                    { title: 'Tracking', dataIndex: 'trackingNumber', render: (v: string) => v ?? '-' },
                    { title: 'Error', dataIndex: 'error', render: (v: string) => v ? <span style={{ color: '#cf1322' }}>{v}</span> : '-' },
                  ]}
                />
              </>
            )}
          </>
        )}
      </Drawer>
    </>
  );
}
